import { workspaceFeatures } from "../lib/workspaceCapabilities";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import FloatingChatAnimation from '../components/FloatingChatAnimation';
import { formatPlanAmount, normalizePlanCurrency, planPeriodLabel } from '../lib/planPricing';
import './LandingPage.css';

// ── Compound Interest Calculator ──────────────────────────────────────────────

const CURRENCIES = {
  COP: { symbol: '$',   locale: 'es-CO', label: 'COP – Peso Col.' },
  USD: { symbol: 'US$', locale: 'en-US', label: 'USD – Dólar'    },
  EUR: { symbol: '€',   locale: 'de-DE', label: 'EUR – Euro'      },
  MXN: { symbol: 'MX$', locale: 'es-MX', label: 'MXN – Peso Mex.' },
};

// Initial investment defaults per currency (reasonable amounts)
const CURRENCY_INIT = { COP: 10000000, USD: 5000, EUR: 5000, MXN: 100000 };
const CURRENCY_MONTHLY = { COP: 500000, USD: 200, EUR: 200, MXN: 3000 };
const CURRENCY_MAX_INIT = { COP: 20000000, USD: 100000, EUR: 100000, MXN: 2000000 };
const CURRENCY_STEP_INIT = { COP: 100000, USD: 1000, EUR: 1000, MXN: 10000 };
const CURRENCY_MAX_MONTHLY = { COP: 5000000, USD: 10000, EUR: 10000, MXN: 100000 };
const CURRENCY_STEP_MONTHLY = { COP: 50000, USD: 100, EUR: 100, MXN: 1000 };

const fmtCurrency = (amount, currency) => {
  const cfg = CURRENCIES[currency];
  return new Intl.NumberFormat(cfg.locale, {
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(Math.round(Math.abs(amount)));
};

// Slider range → percentage (for background gradient fill)
const sliderPct = (val, min, max) => `${((val - min) / (max - min)) * 100}%`;

// Correct compound interest: balance compounds at frequency n per year,
// contributions are added each period (not just annually).
const computeCompound = ({ initial, monthly, rate, years, frequency }) => {
  // n = compounding periods per year
  const n = frequency === 'daily' ? 365 : frequency === 'monthly' ? 12 : 1;
  const rPerPeriod = (rate / 100) / n;
  // Contribution per period (monthly amount split across periods)
  const contribPerPeriod = monthly / (n / 12);
  const totalPeriods = years * n;

  let balance = initial;
  const yearlyData = [{ year: 0, balance: initial, invested: initial, interest: 0 }];

  for (let p = 1; p <= totalPeriods; p++) {
    // Interest earned this period THEN contribution added
    balance = balance * (1 + rPerPeriod) + contribPerPeriod;
    // Record at each year-end
    if (p % n === 0) {
      const yr = p / n;
      const invested = initial + monthly * 12 * yr;
      yearlyData.push({ year: yr, balance, invested, interest: balance - invested });
    }
  }

  const totalInvested = initial + monthly * 12 * years;
  const totalInterest = balance - totalInvested;
  return { finalBalance: balance, totalInvested, totalInterest, yearlyData };
};

const CompoundCalculator = () => {
  const [currency, setCurrency] = useState('COP');
  const [initial, setInitial] = useState(CURRENCY_INIT['COP']);
  const [monthly, setMonthly] = useState(CURRENCY_MONTHLY['COP']);
  const [rate, setRate] = useState(8);
  const [years, setYears] = useState(15);
  const [frequency, setFrequency] = useState('monthly');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  // Update defaults when currency changes
  const handleCurrencyChange = (cur) => {
    setCurrency(cur);
    setInitial(CURRENCY_INIT[cur]);
    setMonthly(CURRENCY_MONTHLY[cur]);
  };

  const maxInitial = CURRENCY_MAX_INIT[currency];
  const stepInitial = CURRENCY_STEP_INIT[currency];
  const maxMonthly = CURRENCY_MAX_MONTHLY[currency];
  const stepMonthly = CURRENCY_STEP_MONTHLY[currency];
  const sym = CURRENCIES[currency].symbol;

  const { finalBalance, totalInvested, totalInterest, yearlyData } = computeCompound({
    initial, monthly, rate, years, frequency
  });

  useEffect(() => { setAnimKey(k => k + 1); }, [finalBalance]);

  const returnPct = totalInvested > 0 ? ((totalInterest / totalInvested) * 100).toFixed(1) : '0.0';

  // SVG chart paths
  const W = 400, H = 160;
  const maxBal = Math.max(...yearlyData.map(d => d.balance), 1);
  const toXY = (d, i) => {
    const x = yearlyData.length > 1 ? (i / (yearlyData.length - 1)) * W : 0;
    const y = H - (d.balance / maxBal) * (H - 12);
    return `${x},${y}`;
  };
  const pts = yearlyData.map(toXY);
  const linePath = `M ${pts.join(' L ')}`;
  const areaPath = `M 0,${H} L ${pts.join(' L ')} L ${W},${H} Z`;
  const invPts = yearlyData.map((d, i) => {
    const x = yearlyData.length > 1 ? (i / (yearlyData.length - 1)) * W : 0;
    const y = H - (d.invested / maxBal) * (H - 12);
    return `${x},${y}`;
  });
  const invPath = `M ${invPts.join(' L ')}`;
  const yearLabels = [0, Math.round(years * 0.33), Math.round(years * 0.66), years];

  // Slider background: brand fill from left to thumb position
  const sliderBg = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, var(--lp-primary) 0%, var(--lp-primary) ${pct}%, #E2EAFD ${pct}%, #E2EAFD 100%)`;
  };

  return (
    <section id="calculator" className="lp-calculator-section">
      <div className="lp-calculator-glow-1"></div>
      <div className="lp-calculator-glow-2"></div>
      
      {/* Header */}
      <div className="text-center mb-16 space-y-4 relative z-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border font-bold text-xs uppercase tracking-widest" style={{color: 'var(--lp-primary)', background:'rgba(57,107,233,0.06)',borderColor:'rgba(57,107,233,0.15)'}}>
          <span className="material-symbols-outlined text-sm">trending_up</span>
          Herramienta gratuita
        </div>
        <h2 className="text-4xl md:text-5xl font-headline font-extrabold text-on-surface">
          El poder del <span style={{color: 'var(--lp-primary)'}}>interés compuesto</span>
        </h2>
        <p className="text-on-surface-variant max-w-xl mx-auto text-lg leading-relaxed">
          Descubre cómo crece tu dinero con el tiempo. Ajusta los parámetros y observa la magia del crecimiento exponencial.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative z-10">
        {/* ── Left: Inputs ── */}
        <div className="lg:col-span-5 space-y-5">
          <div className="lp-glass-card">
            <div className="flex items-center justify-between mb-7">
              <h3 className="font-headline text-xl font-bold text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">tune</span>
                Parámetros
              </h3>
              {/* Currency Selector */}
              <div className="flex gap-1.5">
                {Object.keys(CURRENCIES).map(cur => (
                  <button key={cur} onClick={() => handleCurrencyChange(cur)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${
                        currency === cur
                          ? 'text-white'
                          : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                      }`}
                      style={currency === cur ? { background: 'var(--lp-primary)' } : {}}
                    >
                      {cur}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-7">
              {/* Inversión Inicial */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Inversión Inicial</label>
                  <div className="flex items-center gap-1.5 bg-surface-container-low px-3 py-1.5 rounded-xl">
                    <span className="text-on-surface-variant text-sm font-medium">{sym}</span>
                    <input
                      className="w-28 bg-transparent text-right font-headline font-bold text-primary text-sm focus:outline-none"
                      type="text"
                      value={fmtCurrency(initial, currency)}
                      onChange={e => {
                        const v = parseInt(e.target.value.replace(/\D/g, '')) || 0;
                        setInitial(Math.min(maxInitial, v));
                      }}
                    />
                  </div>
                </div>
                <input
                  type="range" min="0" max={maxInitial} step={stepInitial} value={initial}
                  onChange={e => setInitial(+e.target.value)}
                  className="w-full calc-slider"
                  style={{ background: sliderBg(initial, 0, maxInitial) }}
                />
                <div className="flex justify-between text-[10px] text-on-surface-variant/60 font-medium">
                  <span>{sym}0</span>
                  <span>{sym}{fmtCurrency(maxInitial, currency)}</span>
                </div>
              </div>

              {/* Aportes Mensuales */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Aportes Mensuales</label>
                  <div className="flex items-center gap-1.5 bg-surface-container-low px-3 py-1.5 rounded-xl">
                    <span className="text-on-surface-variant text-sm font-medium">{sym}</span>
                    <input
                      className="w-28 bg-transparent text-right font-headline font-bold text-primary text-sm focus:outline-none"
                      type="text"
                      value={fmtCurrency(monthly, currency)}
                      onChange={e => {
                        const v = parseInt(e.target.value.replace(/\D/g, '')) || 0;
                        setMonthly(Math.min(maxMonthly, v));
                      }}
                    />
                  </div>
                </div>
                <input
                  type="range" min="0" max={maxMonthly} step={stepMonthly} value={monthly}
                  onChange={e => setMonthly(+e.target.value)}
                  className="w-full calc-slider"
                  style={{ background: sliderBg(monthly, 0, maxMonthly) }}
                />
                <div className="flex justify-between text-[10px] text-on-surface-variant/60 font-medium">
                  <span>{sym}0</span>
                  <span>{sym}{fmtCurrency(maxMonthly, currency)}/mes</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider block">Tasa Anual (%)</label>
                  <input
                    type="number" step="0.1" min="0.1" max="100" value={rate}
                    onChange={e => setRate(Math.max(0.1, Math.min(100, +e.target.value)))}
                    className="w-full px-4 py-2.5 bg-surface-container-low border-none rounded-xl font-headline font-bold text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider block">Años</label>
                  <input
                    type="number" min="1" max="50" value={years}
                    onChange={e => setYears(Math.max(1, Math.min(50, +e.target.value)))}
                    className="w-full px-4 py-2.5 bg-surface-container-low border-none rounded-xl font-headline font-bold text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Frecuencia de capitalización */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider block">
                  Capitalización (interés se acumula…)
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[['daily','📅 Diaria'],['monthly','📆 Mensual'],['yearly','📊 Anual']].map(([val, label]) => (
                    <button key={val} onClick={() => setFrequency(val)}
                      className={`py-2.5 rounded-xl text-xs font-bold transition-all ${
                        frequency === val
                          ? 'shadow-sm text-white'
                          : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                      }`}
                      style={frequency === val ? { background: 'var(--lp-primary)' } : {}}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-on-surface-variant/60 italic">
                  {frequency === 'daily' && 'El interés se aplica cada día sobre el saldo acumulado.'}
                  {frequency === 'monthly' && 'El interés se aplica cada mes sobre el saldo acumulado.'}
                  {frequency === 'yearly' && 'El interés se aplica una vez al año sobre el saldo total.'}
                </p>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-secondary-container/20 rounded-2xl p-5 flex gap-3 items-start border border-secondary/10">
            <span className="material-symbols-outlined text-secondary text-xl shrink-0 mt-0.5">lightbulb</span>
            <p className="text-xs text-on-secondary-container leading-relaxed">
              <strong>¿Sabías?</strong> La capitalización <em>diaria</em> produce más que la mensual o anual con la misma tasa. Entre más frecuente, más trabaja tu dinero.
            </p>
          </div>
        </div>

        {/* ── Right: Results ── */}
        <div className="lg:col-span-7 space-y-5">
          <div className="lp-glass-card">
            {/* Big Number */}
            <div className="mb-8">
              <span className="text-on-surface-variant font-label text-xs font-bold uppercase tracking-widest block mb-2">Monto Final Proyectado</span>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span key={animKey} className="font-headline text-5xl md:text-6xl font-extrabold text-primary animate-count">
                  {sym}{fmtCurrency(finalBalance, currency)}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                  <span className="material-symbols-outlined text-base" style={{fontVariationSettings:"'FILL' 1"}}>trending_up</span>
                  +{returnPct}% retorno total
                </span>
                <span className="text-on-surface-variant text-xs">en {years} años · capitalización {frequency === 'daily' ? 'diaria' : frequency === 'monthly' ? 'mensual' : 'anual'}</span>
              </div>
            </div>

            {/* SVG Chart */}
            <div className="relative rounded-2xl overflow-hidden mb-8" style={{height:'180px', background: 'rgba(255,255,255,0.4)'}}>
              <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full">
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#396BE9" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#396BE9" stopOpacity="0.02"/>
                  </linearGradient>
                </defs>
                <path d={`M 0,${H} L ${invPts.join(' L ')} L ${W},${H} Z`} fill="rgba(57,107,233,0.06)"/>
                <path d={areaPath} fill="url(#areaGrad)"/>
                <path d={invPath} fill="none" stroke="#A0B6F5" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.8"/>
                <path d={linePath} fill="none" stroke="#396BE9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="absolute bottom-2 inset-x-4 flex justify-between text-[10px] text-on-surface-variant/50 font-bold uppercase tracking-wider pointer-events-none">
                {yearLabels.map(y => <span key={y}>Año {y}</span>)}
              </div>
              <div className="absolute top-3 right-4 flex items-center gap-4 text-[10px] font-semibold text-on-surface-variant">
                <span className="flex items-center gap-1.5"><span className="w-5 h-0.5 inline-block rounded" style={{background: 'var(--lp-primary)'}}/>Capital Total</span>
                <span className="flex items-center gap-1.5"><span className="w-5 h-0.5 inline-block rounded" style={{background: '#A0B6F5'}}/>Invertido</span>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="p-5 rounded-2xl border-l-4" style={{background: 'rgba(255,255,255,0.5)', borderColor: '#A0B6F5'}}>
                <span className="text-[10px] text-on-surface-variant font-bold uppercase tracking-widest block mb-2">Total Invertido</span>
                <div className="font-headline text-2xl font-extrabold text-on-surface">{sym}{fmtCurrency(totalInvested, currency)}</div>
                <div className="text-xs text-on-surface-variant/70 mt-1">Capital + Aportes</div>
              </div>
              <div className="p-5 rounded-2xl border-l-4" style={{background: 'rgba(255,255,255,0.5)', borderColor: 'var(--lp-primary)'}}>
                <span className="text-[10px] text-on-surface-variant font-bold uppercase tracking-widest block mb-2">Intereses Ganados</span>
                <div className="font-headline text-2xl font-extrabold text-on-surface">{sym}{fmtCurrency(totalInterest, currency)}</div>
                <div className="text-xs font-bold mt-1" style={{color: 'var(--lp-primary)'}}>+{returnPct}% de retorno</div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="space-y-2 mb-8">
              <div className="flex justify-between text-xs font-semibold text-on-surface-variant">
                <span>Capital propio</span><span>Intereses generados</span>
              </div>
              <div className="h-3 rounded-full overflow-hidden flex" style={{background: 'rgba(255,255,255,0.5)'}}>
                <div className="h-full rounded-full transition-all duration-700"
                  style={{width: `${Math.min(99, (totalInvested/Math.max(finalBalance,1))*100)}%`, background: '#A0B6F5'}}/>
                <div className="h-full flex-1 rounded-full" style={{background: 'var(--lp-primary)'}}/>
              </div>
              <div className="flex justify-between text-[10px] text-on-surface-variant/60">
                <span>{finalBalance > 0 ? ((totalInvested/finalBalance)*100).toFixed(1) : 0}% aportado por ti</span>
                <span>{finalBalance > 0 ? ((totalInterest/finalBalance)*100).toFixed(1) : 0}% en intereses</span>
              </div>
            </div>

            {/* Breakdown toggle */}
            <div className="pt-5 border-t border-surface-container-high flex justify-between items-center">
              <span className="text-xs text-on-surface-variant/60 italic">
                Capitalización {frequency === 'daily' ? 'diaria' : frequency === 'monthly' ? 'mensual' : 'anual'} · {currency}
              </span>
              <button onClick={() => setShowBreakdown(b => !b)}
                className="flex items-center gap-1 text-primary font-bold text-sm hover:underline">
                {showBreakdown ? 'Ocultar' : 'Ver desglose anual'}
                <span className="material-symbols-outlined text-lg">{showBreakdown ? 'expand_less' : 'chevron_right'}</span>
              </button>
            </div>
          </div>

          {/* Yearly breakdown table */}
          {showBreakdown && (
            <div className="bg-white rounded-2xl shadow-sm border border-surface-container-high overflow-hidden">
              <div className="p-6 border-b border-surface-container-high">
                <h4 className="font-headline font-bold text-on-surface">Desglose Anual · {CURRENCIES[currency].label}</h4>
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-container-low sticky top-0">
                    <tr>
                      {['Año','Balance','Invertido','Intereses','Rendimiento'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyData.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-surface-container-low/50'}>
                        <td className="px-4 py-3 font-bold text-on-surface">{row.year}</td>
                        <td className="px-4 py-3 text-primary font-semibold">{sym}{fmtCurrency(row.balance, currency)}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{sym}{fmtCurrency(row.invested, currency)}</td>
                        <td className="px-4 py-3 text-tertiary font-semibold">{sym}{fmtCurrency(row.interest, currency)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${row.interest > 0 ? 'bg-green-50 text-green-700' : 'bg-surface-container text-on-surface-variant'}`}>
                            {row.invested > 0 ? ((row.interest / row.invested)*100).toFixed(1) : '0.0'}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // WhatsApp bot number
  const WHATSAPP_NUMBER = "573232467898";
  const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hola, quiero empezar a organizar mis finanzas')}`;

  // Fetch plans on mount
  useEffect(() => {
    // Force light mode logic exclusively for Landing Page
    const html = document.documentElement;
    const wasDark = html.classList.contains("dark");
    if (wasDark) {
      html.classList.remove("dark");
    }

    // Keep it light! Prevent other effects/hooks from turning it dark
    const observer = new MutationObserver(() => {
      if (html.classList.contains("dark")) {
        html.classList.remove("dark");
      }
    });
    observer.observe(html, { attributes: true, attributeFilter: ["class"] });

    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/billing/plans`);
        setPlans(res.data.plans || []);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();

    return () => {
      observer.disconnect();
      // Restore previous state upon leaving landing page
      if (wasDark) {
        html.classList.add("dark");
      }
    };
  }, []);

  const handleLogin = () => navigate('/login');
  const handleWhatsApp = () => window.open(WHATSAPP_LINK, '_blank');

   const handleCalculate = () => {
    let currentBalance = parseFloat(initialInvestment);
    const mRate = parseFloat(monthlyRate) / 100;
    const months = parseInt(timeInMonths);
    const mContribution = parseFloat(monthlyContribution);

    for (let i = 0; i < months; i++) {
      currentBalance = currentBalance * (1 + mRate) + mContribution;
    }
    setFinalAmount(currentBalance.toFixed(2));
  };

  useEffect(() => {
    // Intersection Observer para las tarjetas de Beneficios en Móvil
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active-mobile');
        } else {
          entry.target.classList.remove('active-mobile');
        }
      });
    }, {
      rootMargin: "-45% 0px -45% 0px" // Trigger in the middle 10% of the screen
    });

    const cards = document.querySelectorAll('.lp-benefit-card');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-container">
      {/* ─── NAVBAR ─── */}
      <nav className="lp-navbar">
        <div className="lp-nav-logo">
          <a href="#inicio" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/logo-claro.png" alt="Aorra" style={{ height: "32px", objectFit: "contain" }} />
          </a>
        </div>
        <div className="lp-nav-links">
          <a href="#caracteristicas">Cómo funciona</a>
          <a href="#beneficios">Beneficios</a>
          <a href="#precios">Precios</a>
          <a href="#testimonios">Testimonios</a>
          <a href="#calculator">Calculadora</a>
        </div>
        <div className="lp-nav-actions">
          <button className="lp-btn-login" onClick={handleLogin}>Iniciar sesión</button>
          <button className="lp-btn-primary" onClick={handleWhatsApp}>Empieza ahora</button>
        </div>
      </nav>

      {/* ─── HERO SECTION ─── */}
      <header id="inicio" className="lp-hero-section">
        <div className="glowing-blob-1"></div>
        <div className="glowing-blob-2"></div>
        <div className="lp-hero-left">
          <div className="lp-section-badge">🚀 GESTIÓN FINANCIERA INTELIGENTE</div>
          <div className="lp-hero-text-block">
            <h1 className="lp-hero-title">Tu asistente financiero por WhatsApp.</h1>
            <p className="lp-hero-subtitle">
              Aorra organiza tus gastos y te hace caer en cuenta de esos “gasticos” que al final no eran tan chiquitos. Entiende en qué se te va la plata y toma mejores decisiones.
            </p>
          </div>
          <div className="lp-hero-ctas">
            <button className="lp-btn-primary" onClick={handleWhatsApp}>Registrar primer movimiento</button>
          </div>
          <div className="lp-social-proof">
            <div className="lp-avatars">
              <div className="avatar" style={{backgroundImage: "url('/avatares/mujer-1.png')"}}></div>
              <div className="avatar" style={{backgroundImage: "url('/avatares/hombre-1.png')"}}></div>
              <div className="avatar" style={{backgroundImage: "url('/avatares/mujer-2.png')"}}></div>
            </div>
            <div className="lp-social-text">
              <p style={{margin: 0, fontWeight: 500}}>Únete a más de 15.000 personas que están aprendiendo a manejar mejor su plata por WhatsApp.</p>
            </div>
          </div>
        </div>
        <div className="lp-hero-right">
          <div className="lp-dashboard-preview" style={{ background: 'linear-gradient(135deg, rgba(230, 240, 255, 0.6) 0%, rgba(240, 245, 250, 0.4) 100%)' }}>
            <div className="lp-dashboard-card" style={{ padding: '0', background: 'transparent', boxShadow: 'none', width: '100%', maxWidth: '560px' }}>
              <div className="lp-db-header" style={{ background: '#FFF', borderRadius: '16px', padding: '16px 24px', marginBottom: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <div className="lp-db-profile">
                  <div className="lp-db-avatar">
                    <img src="/landing/9a01ea96596ad74c7b536bc3bcb743e63589f0cf.png" alt="Avatar" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
                  </div>
                  <div>
                    <h3 className="lp-db-name" style={{fontSize: '16px'}}>¡Hola Camilo!</h3>
                    <p className="lp-db-status" style={{fontSize: '12px'}}>Tu balance está en verde hoy</p>
                  </div>
                </div>
                <div className="lp-db-score" style={{fontSize: '13px'}}>Score: 95/100</div>
              </div>
              <div>
                 <img src="/landing/c0cd81e6bcd7ba70fadf589dd27da728c3449dba.png" alt="Gastos" style={{width: '100%', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)'}} />
              </div>
            </div>
            
            {/* Floating assets with circular white backgrounds */}
            <div className="floating-icon" style={{position: 'absolute', top: '-5%', right: '-5%', animation: 'float 5s infinite'}}>
              <div className="icon-bg" style={{width: '90px', height: '90px', background: '#FFF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)'}}>
                <img src="/landing/b6aa45d010c45e8cf8222f53b441b10bbae049f5.png" alt="Piggy Bank" style={{width: '60px'}} />
              </div>
            </div>
            <div className="floating-icon" style={{position: 'absolute', bottom: '10%', left: '-10%', animation: 'float 6s infinite reverse'}}>
              <div className="icon-bg" style={{width: '90px', height: '90px', background: '#FFF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)'}}>
                <img src="/landing/b1d77f481a0efc14f43a54ec2e27a2c500210fe4.png" alt="Money" style={{width: '60px'}} />
              </div>
            </div>
            <div className="floating-icon" style={{position: 'absolute', bottom: '-15%', right: '5%', animation: 'float 7s infinite'}}>
              <div className="icon-bg" style={{width: '110px', height: '110px', background: '#FFF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)'}}>
                <img src="/landing/4724b9570b96274be6f5f0238bffc09606372a89.png" alt="Rocket" style={{width: '80px'}} />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ─── CARACTERISTICAS (PASO A PASO) ─── */}
      <section id="caracteristicas" className="lp-features-section">
        <div className="lp-features-img">
          <div className="lp-features-shape"></div>
          <FloatingChatAnimation />
        </div>
        <div className="lp-features-content">
          <div className="lp-section-badge" style={{color: 'var(--lp-primary)'}}>🎯 PASO A PASO</div>
          <h2 className="lp-features-title">En tres pasos, descubre cómo usas tu dinero.</h2>
          <div className="lp-steps-list">
            <div className="lp-step lp-step-1">
              <div className="lp-step-icon">1</div>
              <div className="lp-step-text">
                <h4>Cuéntale a Aorra lo que gastaste</h4>
                <p>Escríbelo, mándale una nota de voz o envíale una foto de la factura por whatsapp. Aorra se encarga del resto.</p>
              </div>
            </div>
            <div className="lp-step lp-step-2">
              <div className="lp-step-icon">2</div>
              <div className="lp-step-text">
                <h4>Descubre qué se está llevando tu plata</h4>
                <p>Aorra organiza tus gastos por categorías y te muestra dónde se te está yendo más.</p>
              </div>
            </div>
            <div className="lp-step lp-step-3">
              <div className="lp-step-icon">3</div>
              <div className="lp-step-text">
                <h4>Recibe alertas y recomendaciones</h4>
                <p>Cuando un gasto empieza a crecer más de la cuenta, Aorra te avisa y te ayuda a corregir el rumbo.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BENEFICIOS CLAVE ─── */}
      <section id="beneficios" className="lp-benefits-section">
        <div className="lp-benefits-glow-1"></div>
        <div className="lp-benefits-glow-2"></div>
        <div className="lp-section-header relative z-10">
          <span className="lp-section-overline">Beneficios Clave</span>
          <h2>Tus finanzas <span style={{color: 'var(--lp-primary)'}}>no necesitan otra app</span> que nunca vas a abrir.</h2>
          <p>Habla con Aorra como hablas con cualquier contacto en whatsapp. Escribe un gasto, manda una nota de voz o envía una foto de la factura. Sin menús complicados, sin aprender a usar otra plataforma y sin excusas para dejarlo para después.</p>
        </div>
        <div className="lp-benefits-grid relative z-10">
          <div className="lp-benefit-card">
            <div className="lp-benefit-accent"></div>
            <div className="lp-benefit-header">
              <div className="lp-benefit-icon"><img src="/Iconos/Dashboard/balance.png" style={{width: '42px', height: '42px', objectFit: 'contain'}} alt="Dashboard" /></div>
              <div className="lp-benefit-badge">SIN OTRA APP</div>
            </div>
            <h3>No tienes que descargar nada</h3>
            <p>Aorra funciona en WhatsApp, una aplicación que ya conoces y utilizas todos los días.</p>
          </div>
          <div className="lp-benefit-card">
            <div className="lp-benefit-accent"></div>
            <div className="lp-benefit-header">
              <div className="lp-benefit-icon"><img src="/Iconos/scroe financiero/score fianciero.png" style={{width: '42px', height: '42px', objectFit: 'contain'}} alt="Score" /></div>
              <div className="lp-benefit-badge">FÁCIL DE USAR</div>
            </div>
            <h3>Registra un gasto como prefieras</h3>
            <p>Escríbelo, envía una nota de voz o toma una foto de la factura. Aorra se encarga de organizarlo.</p>
          </div>
          <div className="lp-benefit-card">
            <div className="lp-benefit-accent"></div>
            <div className="lp-benefit-header">
              <div className="lp-benefit-icon"><img src="/Iconos/Categorias/Ahorros.png" style={{width: '42px', height: '42px', objectFit: 'contain'}} alt="Ahorro" /></div>
              <div className="lp-benefit-badge">SIEMPRE A LA MANO</div>
            </div>
            <h3>Pregunta y recibe respuestas al instante</h3>
            <p>Consulta cuánto has gastado, cuánto tienes disponible o cómo va una meta sin buscar entre menús y gráficas.</p>
          </div>
          <div className="lp-benefit-card">
            <div className="lp-benefit-accent"></div>
            <div className="lp-benefit-header">
              <div className="lp-benefit-icon"><img src="/Iconos/acciones rapidas/suscripciones.png" style={{width: '42px', height: '42px', objectFit: 'contain'}} alt="Suscripciones" /></div>
              <div className="lp-benefit-badge">MENSAJES ÚTILES</div>
            </div>
            <h3>Aorra te escribe antes de que sea tarde</h3>
            <p>Recibe alertas, recordatorios y recomendaciones directamente en una conversación que sí vas a ver.</p>
          </div>
        </div>
      </section>

      {/* ─── PRECIOS ─── */}
      <section id="precios" className="lp-pricing-section">
        <div className="lp-pricing-glow-1"></div>
        <div className="lp-pricing-glow-2"></div>
        <div className="lp-section-header" style={{position: 'relative', zIndex: 1}}>
          <span className="lp-section-overline">Comienza Ahora</span>
          <h2>Organiza tu plata sin desorganizar tu bolsillo.</h2>
          <p>Sin costos ocultos. Cancela en cualquier momento.</p>
        </div>
        <div className="lp-pricing-grid">
          {plansLoading ? (
            <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '40px'}}>
              <Loader2 className="animate-spin mx-auto" size={32} color="var(--lp-primary)" />
              <p style={{marginTop: '16px', color: 'var(--lp-text-muted)'}}>Cargando planes...</p>
            </div>
          ) : plans.length > 0 ? (
            plans.map((plan, index) => (
              <div key={plan.id || index} className={`lp-pricing-card ${index === 1 ? 'featured' : ''}`}>
                <div>
                  {index === 1 && <div className="lp-featured-badge">⭐ Más popular</div>}
                  <h3 className="lp-pricing-title">{plan.name}</h3>
                  <div className="lp-pricing-price">
                    {formatPlanAmount(plan.price, plan.currency)}
                    <span style={{fontSize: '16px', color: 'var(--lp-text-muted)'}}>
                      {' '}{normalizePlanCurrency(plan.currency)} / {planPeriodLabel(plan.billing_period)}
                    </span>
                  </div>
                  <p className="lp-pricing-desc">{plan.description}</p>
                  <ul className="lp-pricing-features">
                    {[...(plan.features || []), ...workspaceFeatures(plan)].map((feature, idx) => (
                      <li key={idx}><span className="material-symbols-outlined icon">check_circle</span> {feature}</li>
                    ))}
                  </ul>
                </div>
                <button 
                  className={index === 1 ? "lp-btn-primary" : "lp-btn-outline"} 
                  style={{width: '100%'}} 
                  onClick={plan.price > 0 ? handleWhatsApp : handleLogin}
                >
                  {plan.price > 0 ? "Comenzar Prueba" : "Elegir plan"}
                </button>
              </div>
            ))
          ) : (
            <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '40px'}}>
              <p style={{color: 'var(--lp-text-muted)'}}>No hay planes disponibles en este momento.</p>
            </div>
          )}
        </div>
      </section>

      {/* ─── TESTIMONIOS ─── */}
      <section id="testimonios" className="lp-benefits-section">
        <div className="lp-benefits-glow-1"></div>
        <div className="lp-benefits-glow-2"></div>

        {/* Floating 3D decorative icons */}
        <img src="/landing/4724b9570b96274be6f5f0238bffc09606372a89.png" alt="Estrella" className="lp-floating-asset" style={{top: '5%', right: '8%', width: '80px', opacity: 0.7, animationDelay: '0.5s', filter: 'drop-shadow(0 12px 24px rgba(57,107,233,0.2))'}} />
        <img src="/Iconos/Categorias/Ahorros.png" alt="Ahorro" className="lp-floating-asset" style={{bottom: '8%', left: '3%', width: '80px', opacity: 0.5, animationDelay: '1.5s', filter: 'drop-shadow(0 12px 24px rgba(57,107,233,0.2))'}} />

        <div className="lp-section-header relative z-10">
          <span className="lp-section-overline">Lo que dicen nuestros usuarios</span>
          <h2>Personas reales, <span style={{color: 'var(--lp-primary)'}}>resultados reales</span></h2>
          <p>Más de 15.000 personas ya organizan sus finanzas con Aorra directamente desde WhatsApp.</p>
        </div>

        <div className="lp-testimonials-grid relative z-10">

          <div className="lp-testimonial-card">
            <div className="lp-testimonial-top">
              <div className="lp-testimonial-avatar">
                <img src="/avatares/mujer-1.png" alt="Valeria" />
              </div>
              <div>
                <div className="lp-testimonial-name">Valeria M.</div>
                <div className="lp-testimonial-role">Bogotá, Colombia</div>
              </div>
              <div className="lp-testimonial-icon">
                <img src="/Iconos/Categorias/Ahorros.png" style={{width: '36px', filter: 'drop-shadow(0 6px 12px rgba(57,107,233,0.25))'}} alt="" />
              </div>
            </div>
            <div className="lp-testimonial-stars">★★★★★</div>
            <p className="lp-testimonial-text">"Brayitan, tu app me funcionó perfecto, empecé con la prueba gratis, pero definitivo el sistema de pago es increíble."</p>
          </div>

          <div className="lp-testimonial-card lp-testimonial-featured">
            <div className="lp-testimonial-top">
              <div className="lp-testimonial-avatar">
                <img src="/avatares/hombre-1.png" alt="Camilo" />
              </div>
              <div>
                <div className="lp-testimonial-name">Camilo R.</div>
                <div className="lp-testimonial-role">Medellín, Colombia</div>
              </div>
              <div className="lp-testimonial-icon">
                <img src="/landing/b6aa45d010c45e8cf8222f53b441b10bbae049f5.png" style={{width: '36px', filter: 'drop-shadow(0 6px 12px rgba(57,107,233,0.25))'}} alt="" />
              </div>
            </div>
            <div className="lp-testimonial-stars" style={{color: 'var(--lp-text-muted)'}}><span style={{color: 'var(--lp-accent)'}}>★★★★</span>★</div>
            <p className="lp-testimonial-text">"Brayan, el excel me funciona perfecto, pero esto es otro nivel, me gustó cómo se organizó todo muy rápido y crea reportes, muchas gracias."</p>
          </div>

          <div className="lp-testimonial-card">
            <div className="lp-testimonial-top">
              <div className="lp-testimonial-avatar">
                <img src="/avatares/mujer-2.png" alt="Laura" />
              </div>
              <div>
                <div className="lp-testimonial-name">Laura S.</div>
                <div className="lp-testimonial-role">Cali, Colombia</div>
              </div>
              <div className="lp-testimonial-icon">
                <img src="/landing/4724b9570b96274be6f5f0238bffc09606372a89.png" style={{width: '36px', filter: 'drop-shadow(0 6px 12px rgba(57,107,233,0.25))'}} alt="" />
              </div>
            </div>
            <div className="lp-testimonial-stars">★★★★★</div>
            <p className="lp-testimonial-text">"Le mandé una foto al chat, y me la registró completa y dividió las categorías, eso me ahorró mucho tiempo, qué maravilla."</p>
          </div>

        </div>
      </section>

      {/* ─── CTA FINAL ─── */}
      <section className="lp-cta-section">
        <img src="/Iconos/Categorias/Ahorros.png" alt="Ahorro" className="lp-floating-asset" style={{top: '15%', left: '10%', width: '120px', animationDelay: '0s', filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.3))'}} />
        <img src="/landing/4724b9570b96274be6f5f0238bffc09606372a89.png" alt="Star" className="lp-floating-asset" style={{top: '-60px', right: '15%', width: '130px', animationDelay: '2s', zIndex: 20, filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.3))'}} />
        <img src="/landing/b1d77f481a0efc14f43a54ec2e27a2c500210fe4.png" alt="Money" className="lp-floating-asset" style={{bottom: '-60px', left: '15%', width: '150px', animationDelay: '1s', zIndex: 20, filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.3))'}} />
        <img src="/Iconos/scroe financiero/score fianciero.png" alt="Score" className="lp-floating-asset" style={{bottom: '15%', right: '10%', width: '130px', animationDelay: '1.5s', filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.3))'}} />
        <div className="lp-section-header relative z-10" style={{maxWidth: '800px', margin: '0 auto', textAlign: 'center'}}>
          <h2 className="lp-cta-title">¿Listo para empezar?</h2>
          <p style={{color: 'rgba(255,255,255,0.9)', fontSize: '20px'}}>Prueba Aorra sin descargas, en whatsapp, rápido y con tu dosis justa de regaño.</p>
        </div>
        <div style={{display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '40px', position: 'relative', zIndex: 10}}>
          <button className="lp-btn-white" onClick={handleWhatsApp}>Empezar gratis</button>
        </div>
      </section>

      <CompoundCalculator />

      {/* ─── FOOTER ─── */}
      <footer style={{background: 'var(--lp-primary)', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', overflow: 'hidden'}}>
         <img src="/Iconos/Viajes/playa.png" alt="Playa" className="lp-floating-asset" style={{top: '10%', right: '10%', width: '100px', animationDelay: '1s', filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.3))'}} />
         <div style={{display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', position: 'relative', zIndex: 10}}>
           <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
             <img src="/logo-blanco.png" alt="Aorra" style={{ height: "40px", objectFit: "contain" }} />
           </div>
           <p style={{color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0, textAlign: 'center'}}>Aorra todos los derechos reservados 2026 ©</p>
         </div>
      </footer>
    </div>
  );
};

export default LandingPage;
